import { useMemo, useRef } from 'react';
import { useCapabilities } from '../lib/capabilities.js';
import { canSeeNavEntry, canSeeTab, type AccessContext } from './navAccess.js';

export function useAccessContext(): AccessContext {
  const caps = useCapabilities();
  const lastReady = useRef<AccessContext | null>(null);
  const current = useMemo<AccessContext>(
    () => ({ ready: caps.isReady, allowedKeys: caps.allowedKeys, roleKeys: caps.roleKeys }),
    [caps],
  );
  // `/me` refetches on window focus and the capabilities drop back to "not
  // ready" meanwhile; keep the last loaded keys so hidden tabs do not flash back.
  if (current.ready) lastReady.current = current;
  return current.ready ? current : (lastReady.current ?? current);
}

/** Drops the tabs of `moduleId` the signed-in user holds no key for. */
export function useVisibleTabs<T extends { readonly id: string }>(
  moduleId: string,
  tabs: readonly T[],
): readonly T[] {
  const context = useAccessContext();
  return useMemo(
    () => tabs.filter((tab) => canSeeTab(moduleId, tab.id, context)),
    [moduleId, tabs, context],
  );
}

export function useCanSeeNavEntry(entryId: string): boolean {
  const context = useAccessContext();
  return canSeeNavEntry(entryId, context);
}
